import React from 'react';
import { Grid, Select, Modal, Form, Input, Alert, DatePicker, Toast } from '@roo/roo';
import dayJs, { Dayjs } from 'dayjs';
import {
    IncrementFeeItem,
    CONTRACT_TYPE,
    FORM_TIP,
    getSelectablePeriod,
    getSettlementRuleByPeriod,
    IEffectHostingInfo,
    BaseFeeInfoTypeEnum,
    CommissionInfoTypeEnum,
    IEffectHostingInfoSignFormValue,
} from '../api';
import SignFieldArray from './SignFieldArray';
import EffectHostingSignForm from './EffectHostingSignForm';
import './SignModal.scss';

const { Field } = Form;
const { Row, Col } = Grid;
const { RangePicker } = DatePicker;

interface IProps {
    visible: boolean;
    /* 合同类型 */
    coopMode: number;
    // 合作模式
    contractName: string;
    onCancel: () => void;
    onConfirm: (dara: any) => Promise<any>;
    providerId: number;
    id: number;
}

enum SignType {
    // 1-官方代运营扶持期原有合同
    FOSTER = CONTRACT_TYPE.FOSTER,
    // 按效计费代运营合同
    RENEWAL = CONTRACT_TYPE.RENEWAL,
    // 官方代运营扶持期新店模式
    FOSTER_NEW_SHOP = CONTRACT_TYPE.FOSTER_NEW_SHOP,
    // 官方代运营扶持期老店模式
    FOSTER_OLD_SHOP = CONTRACT_TYPE.FOSTER_OLD_SHOP,
    // 官方代运营售卖场景新店模式
    OFFICIAL_SELL_NEW_SHOP = CONTRACT_TYPE.OFFICIAL_SELL_NEW_SHOP,
    // 官方代运营售卖场景老店模式
    OFFICIAL_SELL_OLD_SHOP = CONTRACT_TYPE.OFFICIAL_SELL_OLD_SHOP,
    // 代运营按效托管模式
    EFFECT_HOSTING = CONTRACT_TYPE.EFFECT_HOSTING,
    // 直播cps抽佣模式
    LIVE_CPS = CONTRACT_TYPE.LIVE_CPS,
    // 直播全款模式
    LIVE_ALL = CONTRACT_TYPE.LIVE_ALL,
    // c+按效付费
    C_PLUS = CONTRACT_TYPE.C_PLUS,
    // 官方代运营按效计费模式
    EFFECT_OFFICIAL = CONTRACT_TYPE.EFFECT_OFFICIAL,
}

interface FormData {
    startTime: number;
    periodDays: number;
    endTime: number;
    signType: SignType;
    earningsThreshold?: number;
    baseFee?: number;
    incrementFee?: IncrementFeeItem[];
    rangeDate?: number[];
    anchorId?: string;
}

interface IState {
    earningsThresholdError: boolean;
    loading: boolean;
    // 计费周期
    periodType: FORM_TIP;
    periodList: string[];
    baseFeeIncrGmvThreshold: string;
    baseFee: string;
    earningsThreshold: string;
    /** 根据周期选择结算规则 接口请求是否完成 */
    isPeriodComplated: boolean;
    /** 代运营按效托管模式相关信息 */
    effectHostingInfo: IEffectHostingInfo;
}

// 表单字段文案枚举
const formItemTip = {
    earningsThreshold: {
        [FORM_TIP.PERIOD_M]:
            '该项即为当服务周期内商家实收达到要求后，才会开始计费结算，该项需大于等于【基础服务费】的配置能力，可配置为0',
        [FORM_TIP.PERIOD_D]:
            '商家每日实收金额大于等于设置的门槛后 ，则开始计费结算。设置的数值需大于等于【基础服务费】的配置能力。',
    },
    baseFee: {
        [FORM_TIP.PERIOD_M]: '服务周期内按日计费结算，可设置为0',
        [FORM_TIP.PERIOD_D]: '商家每日实收金额达标后，则会按此设置给服务商结算。',
    },
    incrementFee: {
        [FORM_TIP.PERIOD_M]:
            '服务周期内商家的外卖实收金额达到设置条件，则按日计费结算，抽佣比例可设置为0',
        [FORM_TIP.PERIOD_D]: '商家每日实收金额达到设置条件，则按日计费结算。',
    },
};

export default class SupportModal extends React.Component<IProps, IState> {
    constructor(props) {
        super(props);
        this.state = {
            earningsThresholdError: false,
            loading: false,
            periodType: FORM_TIP.PERIOD_D,
            periodList: [],
            baseFeeIncrGmvThreshold: '',
            baseFee: '',
            isPeriodComplated: true,
            earningsThreshold: '',
            effectHostingInfo: {
                baseFeeInfo: {
                    typeValue: BaseFeeInfoTypeEnum.FixedPrice,
                    typeLabel: '',
                    earningsThreshold: '',
                    baseFee: '',
                    deposit: '',
                },
                commissionInfo: {
                    typeLabel: '',
                    typeValue: CommissionInfoTypeEnum.Increment,
                    intervalBegin: '',
                    intervalEnd: '',
                    rate: '',
                },
            },
        };
    }

    formRef: any = null;

    effectFormRef: any = React.createRef();

    defaultPeriodDays = 3; // 默认周期

    initValues: FormData = {
        startTime: Math.floor(Date.now() / 86400000) * 86400 - 28800,
        periodDays: this.defaultPeriodDays,
        endTime:
            Math.floor(Date.now() / 86400000 + this.defaultPeriodDays * 30) * 86400 - 28800 - 1,
        signType: this.props.coopMode || 2, // TODO 啥逻辑？
        incrementFee: [], //初始化表单
        rangeDate: [], // 初始时候日期范围
        anchorId: '', // 初始时候主播id
    };

    componentDidMount() {
        const { id } = this.props;
        // 初始化拿到合同计费周期
        getSelectablePeriod({ distributeId: id }).then(selectableRes => {
            if (selectableRes.data.length > 0) {
                this.formRef.setFieldValue('periodDays', selectableRes.data[0]);
                this.formRef.setFieldValue(
                    'endTime',
                    Math.floor(Date.now() / 86400000 + parseFloat(selectableRes.data[0]) * 30) *
                        86400 -
                        28800 -
                        1,
                );
                this.setState({ periodList: selectableRes.data });
                // 根据周期获取结算规则
                this.handlePeriodChange(parseFloat(selectableRes.data[0]));
            }
        });
    }

    /** 切换周期的时候改变结算规则 */
    handlePeriodChange = (period: number) => {
        const { id } = this.props;
        this.setState({ isPeriodComplated: false });
        getSettlementRuleByPeriod({ distributeId: id, period })
            .then(res => {
                this.setState({
                    baseFeeIncrGmvThreshold: res.data?.baseFeeIncrGmvThreshold ?? '',
                    baseFee: res.data?.baseFee ?? '',
                    earningsThreshold: res.data?.earningsThreshold ?? '',
                    effectHostingInfo: res.data?.effectHostingInfo,
                });
            })
            .catch(err => {
                console.log(err);
            })
            .finally(() => {
                this.setState({
                    isPeriodComplated: true,
                });
            });
    };

    /**
     * 转换按效的相关数据
     * 把金额的部分全部转换成分（*100）
     */
    transformEffectInfoData = (rawData: IEffectHostingInfoSignFormValue) => {
        const d = JSON.parse(JSON.stringify(rawData)) as IEffectHostingInfoSignFormValue;
        const mult = (num: string) => (num === '' ? '' : (Number(num) * 100).toFixed(0).toString());
        d.baseFeeInfo.baseFee = mult(d.baseFeeInfo.baseFee);
        d.baseFeeInfo.deposit = mult(d.baseFeeInfo.deposit);
        d.baseFeeInfo.earningsThreshold = mult(d.baseFeeInfo.earningsThreshold);
        d.commissionInfo.intervalList = d.commissionInfo.intervalList.map(item => {
            const { intervalBegin, intervalEnd, rate } = item;
            return {
                intervalBegin: mult(intervalBegin),
                intervalEnd: mult(intervalEnd),
                rate: mult(rate),
            };
        });
        return d;
    };

    onConfirm = () => {
        const {
            periodType,
            earningsThresholdError,
            baseFeeIncrGmvThreshold,
            baseFee,
            earningsThreshold,
        } = this.state;
        this.formRef.validateForm((error: any) => {
            if (!error) {
                if (earningsThresholdError) {
                    // 门槛和服务费有问题，弹出错误提示
                    Toast.open({
                        title: '数据请求错误',
                        duration: 1000,
                        position: 'topCenter',
                        status: 'fail',
                    });
                    return;
                }
                this.setState({
                    loading: true,
                });
                const rawData = this.formatFormData(this.formRef.state.values);
                if (rawData.baseFee === '' && baseFee !== '') {
                    // 售卖新模式中的basefee是查的接口
                    rawData.baseFee = baseFee;
                }
                if (rawData.earningsThreshold === '' && earningsThreshold !== '') {
                    // 售卖新模式中的earningsThreshold是查的接口
                    rawData.earningsThreshold = earningsThreshold;
                }
                /**
                 * 这里场景的判断需要和下面的effectinfoform展示逻辑保持同步
                 * 调整枚举需要同步变动
                 */
                if (this.effectFormRef.current) {
                    this.effectFormRef.current.validateForm((error: any) => {
                        if (error) {
                            this.setState({
                                loading: false,
                            });
                            return;
                        }
                        const effectFormValues = this.effectFormRef.current.state.values;
                        // console.log(effectFormValues);
                        this.props
                            .onConfirm({
                                ...rawData,
                                periodType,
                                baseFeeIncrGmvThreshold,
                                effectHostingInfo: this.transformEffectInfoData(effectFormValues),
                            })
                            .finally(() => {
                                this.setState({
                                    loading: false,
                                });
                            });
                    });
                } else {
                    this.props
                        .onConfirm({
                            ...rawData,
                            periodType,
                            baseFeeIncrGmvThreshold,
                        })
                        .finally(() => {
                            this.setState({
                                loading: false,
                            });
                        });
                }
            }
        });
    };

    formatFormData = (formData: any) => {
        const {
            startTime,
            endTime,
            earningsThreshold = '',
            baseFee = '',
            incrementFee = [],
            rangeDate,
            anchorId,
        } = formData;
        const { coopMode } = this.props;
        const obj = {
            anchorId,
            startTime,
            endTime,
            earningsThreshold: earningsThreshold === '' ? '' : Number(earningsThreshold) * 100,
            baseFee: baseFee === '' ? '' : Number(baseFee) * 100,
            incrementFee:
                incrementFee.length === 0
                    ? ''
                    : JSON.stringify(
                          incrementFee.map((v: any, index: number) => ({
                              order: index + 1,
                              ladderThreshold: Number(v.ladderThreshold) * 100,
                              ladderCommissionRate: Number(v.ladderCommissionRate),
                          })),
                      ),
        };
        if (coopMode === SignType.LIVE_CPS || coopMode === SignType.LIVE_ALL) {
            /**
             * 直播cps或直播全款模式 日期改为范围选择
             * 用 rangeDate里面的值替换 startTime和endTime
             */
            obj.startTime = dayJs.unix(rangeDate[0]).startOf('day').unix();
            obj.endTime = dayJs.unix(rangeDate[1]).endOf('day').unix();
        }

        return obj;
    };

    incrementFeeValidator = (rule: any, value: IncrementFeeItem[]) => {
        const { earningsThreshold } = this.formRef.state.values;
        if (value.length < 1) {
            return new Error('实收增量收费没有填写');
        }
        for (let i = 0; i < value.length; i++) {
            const val = value[i];
            const lastThreshold = i === 0 ? earningsThreshold : value[i - 1].ladderThreshold;
            if (Number(lastThreshold) >= Number(val.ladderThreshold)) {
                return new Error('下限 <= 商家实收 < 上限');
            }
        }
        return true;
    };

    getEndTime = (startTime: number, periodDays: number) => startTime + periodDays * 30 * 86400 - 1;

    render() {
        const { visible, onCancel, coopMode, contractName } = this.props;
        const {
            periodType,
            periodList,
            baseFeeIncrGmvThreshold,
            baseFee,
            loading,
            isPeriodComplated,
            effectHostingInfo,
        } = this.state;
        return (
            <Modal
                visible={visible}
                onCancel={onCancel}
                confirmButtonProps={{
                    disabled: loading || !isPeriodComplated,
                }}
                onConfirm={this.onConfirm}
                style={{ width: '1200px' }}
                className="sign-modal"
            >
                <Form
                    ref={ref => {
                        this.formRef = ref;
                    }}
                    value={this.initValues}
                    cols={{ label: { span: 3 } }}
                >
                    {({ values, setFieldValue }: { values: FormData; setFieldValue: any }) => (
                        <form>
                            <Field label="签约合同" name="signType">
                                {({ value, handleChange }: any) => (
                                    <Select
                                        value={value}
                                        disabled
                                        options={[
                                            /** data.thirdSign 和 customerSource 为1（官方代运营合同）  */
                                            {
                                                label: contractName,
                                                value: coopMode,
                                            },
                                        ]}
                                        onChange={e => {
                                            const month = e === SignType.FOSTER ? 3 : 1;
                                            setFieldValue('periodDays', month);
                                            setFieldValue(
                                                'endTime',
                                                Math.floor(Date.now() / 86400000 + month * 30) *
                                                    86400 -
                                                    28800 -
                                                    1,
                                            );
                                            handleChange(e);
                                        }}
                                    />
                                )}
                            </Field>
                            {
                                // 直播cps或直播全款模式 日期改为范围选择
                                coopMode === SignType.LIVE_CPS || coopMode === SignType.LIVE_ALL ? (
                                    <>
                                        <Field
                                            label="日期选择"
                                            name="rangeDate"
                                            rule={{
                                                required: true,
                                                message: '这是必填项',
                                            }}
                                        >
                                            {({ handleChange }) => (
                                                <RangePicker
                                                    // 不能选今天以前的日子
                                                    disabledDate={cur =>
                                                        cur.isBefore(dayJs().subtract(1, 'day'))
                                                    }
                                                    onChange={handleChange}
                                                    valueOfType="second"
                                                />
                                            )}
                                        </Field>
                                    </>
                                ) : (
                                    <>
                                        <Field label="服务开始日期" name="startTime">
                                            {({
                                                value,
                                                handleChange,
                                            }: {
                                                value: Dayjs;
                                                handleChange: (v: Dayjs) => void;
                                            }) => (
                                                <DatePicker
                                                    format="YYYY-MM-DD"
                                                    valueOfType="second"
                                                    placeholder="请选择日期"
                                                    value={value}
                                                    disabledDate={cur =>
                                                        cur.isBefore(dayJs().subtract(1, 'day'))
                                                    }
                                                    onChange={(e: any) => {
                                                        setFieldValue(
                                                            'endTime',
                                                            this.getEndTime(e, values.periodDays),
                                                        );
                                                        handleChange(e);
                                                    }}
                                                />
                                            )}
                                        </Field>
                                        <Field label="服务周期" name="periodDays">
                                            {({ value, handleChange }: any) => (
                                                <Select
                                                    value={value}
                                                    options={periodList.map(item => ({
                                                        value: item,
                                                        label: `30天 * ${item}`,
                                                    }))}
                                                    onChange={(e: number) => {
                                                        handleChange(e);
                                                        setFieldValue(
                                                            'endTime',
                                                            this.getEndTime(values.startTime, e),
                                                        );
                                                        this.handlePeriodChange(e);
                                                    }}
                                                />
                                            )}
                                        </Field>
                                        {values.signType === SignType.EFFECT_HOSTING ||
                                        values.signType === SignType.EFFECT_OFFICIAL ? (
                                            <Field label="服务结束日期" name="endTime">
                                                {({
                                                    value,
                                                    handleChange,
                                                }: {
                                                    value: Dayjs;
                                                    handleChange: (v: Dayjs) => void;
                                                }) => (
                                                    <DatePicker
                                                        format="YYYY-MM-DD"
                                                        valueOfType="second"
                                                        placeholder="请选择日期"
                                                        value={value}
                                                        disabledDate={cur =>
                                                            cur.isBefore(
                                                                dayJs(
                                                                    (this.getEndTime(
                                                                        values.startTime,
                                                                        values.periodDays,
                                                                    ) +
                                                                        1) *
                                                                        1000,
                                                                ).subtract(1, 'day'),
                                                            )
                                                        }
                                                        onChange={(e: any) => {
                                                            handleChange(e);
                                                        }}
                                                    />
                                                )}
                                            </Field>
                                        ) : (
                                            <Field label="服务结束日期" name="endTime">
                                                {({ value }: any) => (
                                                    <p className="endtime">
                                                        {dayJs.unix(value).format('YYYY-MM-DD')}
                                                    </p>
                                                )}
                                            </Field>
                                        )}
                                    </>
                                )
                            }

                            {values.signType === SignType.RENEWAL && (
                                <>
                                    <Field
                                        label="基础服务费门槛"
                                        name="earningsThreshold"
                                        rule={{
                                            required: true,
                                            message: '这是必填项',
                                        }}
                                        cols={{ field: { span: 10 } }}
                                    >
                                        {({ value: _value, handleChange }: any) => (
                                            <Row className="earnings-threshold">
                                                <Col span={2}>
                                                    <Input
                                                        type="number"
                                                        value={_value}
                                                        onChange={e => {
                                                            const { value } = e.target;
                                                            let valueN = Number(value);
                                                            if (valueN < 0) {
                                                                return;
                                                            }
                                                            if (valueN > 19999999) {
                                                                valueN = 19999999;
                                                            }
                                                            if (value === '') {
                                                                valueN = 0;
                                                                setFieldValue('incrementFee', [
                                                                    {
                                                                        ladderThreshold: '',
                                                                        ladderCommissionRate: '',
                                                                    },
                                                                ]);
                                                            } else if (
                                                                values.signType === SignType.RENEWAL
                                                            ) {
                                                                if (
                                                                    values.incrementFee &&
                                                                    values.incrementFee.length &&
                                                                    Number(
                                                                        values.incrementFee[0]
                                                                            .ladderThreshold,
                                                                    ) > valueN
                                                                ) {
                                                                    const _ince =
                                                                        values.incrementFee.concat();
                                                                    setFieldValue(
                                                                        'incrementFee',
                                                                        _ince,
                                                                    );
                                                                } else {
                                                                    setFieldValue('incrementFee', [
                                                                        {
                                                                            ladderThreshold: '',
                                                                            ladderCommissionRate:
                                                                                '',
                                                                        },
                                                                    ]);
                                                                }
                                                                // 基础服务费门槛 >= 基础服务费  https://km.sankuai.com/page/1227931079
                                                                if (
                                                                    values.baseFee &&
                                                                    valueN < values.baseFee
                                                                ) {
                                                                    if (
                                                                        !this.state
                                                                            .earningsThresholdError
                                                                    ) {
                                                                        this.setState({
                                                                            earningsThresholdError:
                                                                                true,
                                                                        });
                                                                    }
                                                                } else if (
                                                                    this.state
                                                                        .earningsThresholdError
                                                                ) {
                                                                    this.setState({
                                                                        earningsThresholdError:
                                                                            false,
                                                                    });
                                                                }
                                                            }
                                                            handleChange(valueN.toFixed(0));
                                                        }}
                                                    />
                                                </Col>
                                                <Col className="m-unit" span={1}>
                                                    {`元/${
                                                        periodType === FORM_TIP.PERIOD_M
                                                            ? '月'
                                                            : '日'
                                                    }`}
                                                </Col>
                                                <Col className="tips" span={9}>
                                                    <Alert
                                                        title={
                                                            formItemTip.earningsThreshold[
                                                                periodType
                                                            ]
                                                        }
                                                        icon="roo-icon-info-circle-o"
                                                        type={
                                                            this.state.earningsThresholdError
                                                                ? 'danger'
                                                                : undefined
                                                        }
                                                    />
                                                </Col>
                                            </Row>
                                        )}
                                    </Field>
                                    <Field
                                        label="基础服务费"
                                        name="baseFee"
                                        cols={{ field: { span: 10 } }}
                                        rule={{
                                            required: true,
                                            message: '这是必填项',
                                        }}
                                    >
                                        {({ value, handleChange }: any) => (
                                            <Row className="earnings-threshold">
                                                <Col span={2}>
                                                    <Input
                                                        type="number"
                                                        value={value}
                                                        onChange={e => {
                                                            const _value = e.target.value;
                                                            let valueN = Number(_value);
                                                            if (valueN < 0) {
                                                                return;
                                                            }
                                                            if (valueN > 50000) {
                                                                valueN = 50000;
                                                            }
                                                            if (periodType !== FORM_TIP.PERIOD_M) {
                                                                if (valueN > 100) {
                                                                    valueN = 100;
                                                                }
                                                            }
                                                            if (
                                                                values.earningsThreshold &&
                                                                values.earningsThreshold < valueN
                                                            ) {
                                                                if (
                                                                    !this.state
                                                                        .earningsThresholdError
                                                                ) {
                                                                    this.setState({
                                                                        earningsThresholdError:
                                                                            true,
                                                                    });
                                                                }
                                                            } else if (
                                                                this.state.earningsThresholdError
                                                            ) {
                                                                this.setState({
                                                                    earningsThresholdError: false,
                                                                });
                                                            }
                                                            handleChange(valueN.toFixed(0));
                                                        }}
                                                    />
                                                </Col>
                                                <Col className="m-unit" span={1}>
                                                    {`元/${
                                                        periodType === FORM_TIP.PERIOD_M
                                                            ? '月'
                                                            : '日'
                                                    }`}
                                                </Col>
                                                <Col className="tips" span={9}>
                                                    <Alert
                                                        title={formItemTip.baseFee[periodType]}
                                                        icon="roo-icon-info-circle-o"
                                                    />
                                                </Col>
                                            </Row>
                                        )}
                                    </Field>
                                    <Field
                                        label="实收增量收费"
                                        name="incrementFee"
                                        cols={{ field: { span: 10 } }}
                                        rule={{
                                            validator: this.incrementFeeValidator,
                                        }}
                                    >
                                        {({
                                            value,
                                            handleChange,
                                        }: {
                                            value?: IncrementFeeItem[];
                                            handleChange: (value: IncrementFeeItem[]) => void;
                                        }) => (
                                            <SignFieldArray
                                                value={value}
                                                earningsThreshold={values.earningsThreshold}
                                                handleChange={e => {
                                                    this.formRef.validateField('incrementFee');
                                                    handleChange(e);
                                                }}
                                                periodType={periodType}
                                            />
                                        )}
                                    </Field>
                                    <Alert
                                        title={formItemTip.incrementFee[periodType]}
                                        icon="roo-icon-info-circle-o"
                                    />
                                </>
                            )}
                            {baseFeeIncrGmvThreshold !== '' && (
                                <Field
                                    label="商家实收增量门槛要求"
                                    name="baseFeeIncrGmvThreshold123"
                                >
                                    {() => (
                                        <Input
                                            style={{ width: '200px' }}
                                            disabled
                                            value={`ΔGMV≥${
                                                Number(baseFeeIncrGmvThreshold) / 100
                                            }元/${coopMode === 7 ? '月' : '天'}`}
                                        />
                                    )}
                                </Field>
                            )}
                            {baseFee !== '' && (
                                <Field label="商家每日扣除费用" name="baseFee123">
                                    {() => (
                                        <Input disabled value={`${Number(baseFee) / 100}元/天`} />
                                    )}
                                </Field>
                            )}

                            {
                                /**
                                 * 下面的判断增加一个effectHostingInfo的条件
                                 * 防止EffectHostingSignForm初始化的时候没有拿到最新的effectHostingInfo
                                 * 使用form是防止item中的initval没有办法及时更新
                                 * 这里的枚举值和上面签约的判断保持一致
                                 */
                                effectHostingInfo != null &&
                                    effectHostingInfo.baseFeeInfo != null &&
                                    effectHostingInfo.baseFeeInfo.typeLabel !== '' && (
                                        <EffectHostingSignForm
                                            info={effectHostingInfo}
                                            effectFormRef={this.effectFormRef}
                                            coopMode={coopMode}
                                        />
                                    )
                            }
                            {
                                /**
                                 * 直播cps或直播全款模式 增加主播id相关字段
                                 */
                                (coopMode === SignType.LIVE_CPS ||
                                    coopMode === SignType.LIVE_ALL) && (
                                    <>
                                        <Field
                                            label={
                                                <span
                                                    style={{
                                                        marginLeft:
                                                            coopMode === SignType.LIVE_CPS
                                                                ? '4px'
                                                                : '0',
                                                        marginRight:
                                                            coopMode === SignType.LIVE_CPS
                                                                ? '54px'
                                                                : '0',
                                                    }}
                                                >
                                                    {`${
                                                        coopMode === SignType.LIVE_CPS
                                                            ? '绑定'
                                                            : '关联'
                                                    }主播ID`}
                                                </span>
                                            }
                                            name="anchorId"
                                            layout={
                                                coopMode === SignType.LIVE_CPS
                                                    ? 'inline'
                                                    : 'horizontal'
                                            }
                                            rule={[
                                                {
                                                    required: true,
                                                    message: '这是必填项',
                                                },
                                                {
                                                    pattern: /^\d+$/,
                                                    message: '只能填数字',
                                                },
                                            ]}
                                        >
                                            {({ handleChange }) => (
                                                <Input onChange={handleChange} />
                                            )}
                                        </Field>
                                    </>
                                )
                            }
                        </form>
                    )}
                </Form>
            </Modal>
        );
    }
}
